import {
  FDBodyText, FDBox,
  FDEmptyState,
  FDIcon,
  FDList,
  FDListItem,
  FDListItemText,
  FDListItemTextRightAligned,
  FDStack,
  FDTooltip,
} from '@facet-wealth/design';
import classNames from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { isFieldVisible } from 'Components/UI/Meta/isFieldVisible';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { buildFacetDesignOverride } from 'Packages/FacetRest/FacetDesignOverride';
import Fac from 'Icons/Fac';
import throwInDevOutsideReact from 'Helpers/throwInDevOutsideReact';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { dataAttrs } from 'Helpers/Testing';
import { getBorder } from 'Components/utils';
import { useMobileOrMCLayout } from 'Hooks/useMobileLayoutOrMCLayout';

const metaOverrideSet = buildFacetDesignOverride();

const UPDATE_STATUS_ICON_PROPS = {
  info: { icon: 'info', color: 'info' },
  link: { icon: 'link', color: 'success' },
  linkOff: { icon: 'linkOff', color: 'error' },
};

const useStyles = buildUseStyles('UpdatedBalanceSheetCategoriesAccordionContent', (theme) => ({
  root: {
    '& .FD-MuiTouchRipple-root': {
      display: 'none',
    },
    '& .FD-MuiBox-root > .FD-MuiListItemText-root:last-child': {
      flex: 'unset !important',
      minWidth: '120px',
    },
    '& .FD-MuiListItemButton-root > * > * > *, & .FD-MuiListItemButton-root > * > * > * > *': {
      maxWidth: '100%',
    },
  },
  mobileRoot: {
    '& .FD-MuiListItem-root + .FD-MuiListItem-root': {
      ...getBorder(theme, { top: true }),
    },
    '& .FD-MuiListItemButton-root': {
      paddingLeft: 0,
      paddingRight: 0,
      ':&hover': {
        background: 'transparent !important',
      },
    },
  },
  mobileNoBigNumbersRoot: {
    '& .FD-MuiBox-root > .FD-MuiListItemText-root:last-child': {
      width: '120px',
    },
  },
  desktopOrBigNumbersRoot: {
    '& .FD-MuiBox-root > .FD-MuiListItemText-root:last-child': {
      width: '100%',
      maxWidth: '150px',
    },
  },
  inlineTypography: {
    '& .FD-MuiTypography-root': {
      display: 'inline',
      verticalAlign: 'middle',
    },
  },
  horizontalSpacer: {
    width: theme.spacing(1),
    display: 'inline-block',
  },
  fac: {
    color: theme.palette.facet.orange,
    fontSize: '1em',
  },
  invisible: {
    visibility: 'hidden',
  },
}));

export const UpdatedBalanceSheetCategoriesAccordionContent = ({ hasBigNumbers, lineItems, onEditLineItemClick }) => {
  const isMobileOrMC = useMobileOrMCLayout();
  const classes = useStyles();
  if (lineItems.length < 1) {
    return (
      <FDBox paddingY={1}>
        <FDEmptyState
          icon={'search'}
          title={'No items added for this category'}
          {...dataAttrs('balance-sheet-line-items-empty-state')}
        />
      </FDBox>
    );
  }
  return (
    <div
      className={classNames(classes.root, {
        [classes.mobileRoot]: isMobileOrMC,
        [classes.desktopOrBigNumbersRoot]: !isMobileOrMC || hasBigNumbers,
        [classes.mobileNoBigNumbersRoot]: isMobileOrMC && !hasBigNumbers,
      })}
    >
      <FDList>
        {lineItems.map((lineItemResource) => {
          const hasDetails = isFieldVisible(lineItemResource, lineItemResource.metaField('detail'));
          const color = lineItemResource.isTruthy('disabled') ? 'textDisabled' : undefined;
          const updateStatusIconKey = lineItemResource.attr('updateStatusIcon');
          if (updateStatusIconKey && !(updateStatusIconKey in UPDATE_STATUS_ICON_PROPS)) {
            throwInDevOutsideReact(`Encountered updateStatusIcon ${updateStatusIconKey} on line item resource ${lineItemResource.id()} which is not in the map of icons`);
          }
          const updateStatusIconProps = UPDATE_STATUS_ICON_PROPS[updateStatusIconKey] ?? null;
          const valueFontSize = hasBigNumbers ? 'sm' : 'md';
          const errorIcon = lineItemResource.attr('errorMessage') ? (
            <FDTooltip title={lineItemResource.attr('errorMessage')}><FDIcon icon="error" color="error" /></FDTooltip>
          ) : null;

          return (
            <FDListItem
              key={lineItemResource.id()}
              onClick={lineItemResource.withRelationship(
                fr.relationships.lineItem.name,
                (rel) => () => onEditLineItemClick(rel),
                () => undefined,
              )}
              {...dataAttrs('balance-sheet-line-item')}
            >
              <FDListItemText
                color={lineItemResource.isTruthy('disabled') ? 'disabled' : undefined}
                primaryText={(
                  <div
                    className={classes.inlineTypography}
                  >
                    {errorIcon ? (
                      <>
                        {errorIcon}
                        <div className={classes.horizontalSpacer} />
                      </>
                    ) : null}
                    <FDBodyText color={color} value={lineItemResource.attr('name')} />
                  </div>
                )}
                secondaryText={(
                  <FDStack direction="column">
                    <div className={classes.inlineTypography}>
                      {updateStatusIconProps ? (
                        <>
                          <FDTooltip title={lineItemResource.attr('lastUpdated')}><FDIcon {...updateStatusIconProps} size="md" /></FDTooltip>
                          <div className={classes.horizontalSpacer} />
                        </>
                      ) : null}
                      {lineItemResource.isTruthy('managedByFacet') ? (
                        <>
                          <Fac className={classes.fac} />
                          <div className={classes.horizontalSpacer} />
                        </>
                      ) : null}
                      <FDBodyText
                        color={color}
                        value={hasDetails ? `${lineItemResource.attr('lastUpdatedSummary')} - ${lineItemResource.attr('description')}` : lineItemResource.attr('description')}
                      />
                    </div>
                    {hasDetails ? (<MetaControl attrName={'detail'} resource={lineItemResource} metaOverrideSet={metaOverrideSet} size="sm" color={color} />) : null}
                  </FDStack>
                )}
              />
              <FDListItemTextRightAligned
                color={lineItemResource.isTruthy('disabled') ? 'disabled' : undefined}
                primaryText={(
                  <FDStack direction="row" justifyContent="flex-end">
                    <FDBodyText size={valueFontSize} color={color} align="end" value={lineItemResource.attr('value')} />
                    {/* The accordion summary takes up some extra space with its arrow icon, making this look a little off - let's fix that by adding some blank space the same size as the accordion's arrow */}
                    {isMobileOrMC ? null : <div className={classes.invisible}><FDIcon icon="arrowUp" /></div>}
                  </FDStack>
                )}
              />
            </FDListItem>
          );
        })}
      </FDList>
    </div>
  );
};

UpdatedBalanceSheetCategoriesAccordionContent.propTypes = {
  lineItems: PropTypes.array.isRequired,
  onEditLineItemClick: PropTypes.func.isRequired,
  hasBigNumbers: PropTypes.bool.isRequired,
};
